import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Select, MenuItem } from "@mui/material";
import axios from "axios";
import ApiCallService from "../../../../../Service/ApiCallService";

const normalizeArray = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload ?? [];
  return Array.isArray(data) ? data : [];
};

const extractProgramsFromBasket = (payload) => {
  const basket = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const list =
    basket?.programs || basket?.programList || basket?.items || basket?.children || [];
  return Array.isArray(list) ? list : [];
};

const getSourceRefId = (sourceRef) => {
  if (!sourceRef) return "";
  if (typeof sourceRef === "object") {
    return sourceRef._id || sourceRef.id || sourceRef.$oid || "";
  }
  return sourceRef;
};

const getDesignationLabel = (designation) => {
  if (!designation) return "";
  if (typeof designation === "string") return designation;
  return (
    designation.label ||
    designation.title ||
    designation.name ||
    designation.policyRole ||
    designation._id ||
    designation.id ||
    designation.designationId ||
    ""
  );
};

const getDesignationPolicyRole = (designation) => {
  if (!designation || typeof designation === "string") return "";
  return String(designation.policyRole || designation.role || "")
    .trim()
    .toUpperCase();
};

const normalizePrivileges = (value) => {
  if (!value || typeof value !== "object") return null;
  if (value.modules && typeof value.modules === "object") {
    return { modules: value.modules };
  }
  return { modules: value };
};

const getDesignationPrivileges = (designation) => {
  if (!designation || typeof designation === "string") return null;
  return (
    normalizePrivileges(designation.defaultPrivileges) ||
    normalizePrivileges(designation.privileges) ||
    null
  );
};

const getScopeEntityId = (entity, keys = []) => {
  if (!entity) return "";
  if (typeof entity === "string") return entity;
  if (entity?.$oid) return entity.$oid;
  for (const key of keys) {
    if (entity?.[key]) return entity[key];
  }
  return "";
};

const normalizeIdToken = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getScopeByDesignationId = (designationScopes, designationId) => {
  if (!designationScopes || typeof designationScopes !== "object") return null;
  if (designationScopes[designationId]) return designationScopes[designationId];

  const targetToken = normalizeIdToken(designationId);
  if (!targetToken) return null;

  const matchedKey = Object.keys(designationScopes).find(
    (key) => normalizeIdToken(key) === targetToken,
  );
  return matchedKey ? designationScopes[matchedKey] : null;
};

const normalizeProgramOption = (program) => {
  if (!program) return null;
  if (typeof program === "string") {
    return { _id: program, programName: program };
  }
  const id = program?._id || program?.id || "";
  if (!id) return null;
  return {
    ...program,
    _id: id,
    programName: program?.programName || program?.name || "",
  };
};

const getEntityId = (item, directKey, nestedKey) => {
  const direct = item?.[directKey];
  if (typeof direct === "object") {
    return direct?._id || direct?.id || "";
  }
  if (direct) return direct;
  const nested = item?.[nestedKey];
  if (typeof nested === "object") {
    return nested?._id || nested?.id || "";
  }
  return nested || "";
};

const getEntityName = (entity, keys = []) => {
  if (!entity) return "";
  if (typeof entity !== "object") return entity;
  for (const key of keys) {
    const value = entity?.[key];
    if (!value) continue;
    if (typeof value === "object") {
      const nestedValue = getEntityName(value, keys);
      if (nestedValue) return nestedValue;
      continue;
    }
    return value;
  }
  return entity?.name || entity?.label || "";
};

const normalizeStateOption = (state) => {
  const id = getSourceRefId(state) || getEntityId(state, "stateId", "state");
  if (!id) return null;
  return {
    ...(typeof state === "object" ? state : {}),
    _id: id,
    stateName: getEntityName(state, ["stateName", "name", "label"]) || id,
  };
};

const normalizeRegionOption = (region) => {
  const id = getSourceRefId(region) || getEntityId(region, "regionId", "region");
  if (!id) return null;
  return {
    ...(typeof region === "object" ? region : {}),
    _id: id,
    regionName: getEntityName(region, ["regionName", "name", "label"]) || id,
  };
};

const normalizeDistrictOption = (district) => {
  const id =
    getSourceRefId(district) || getEntityId(district, "districtId", "district");
  if (!id) return null;
  return {
    ...(typeof district === "object" ? district : {}),
    _id: id,
    districtName:
      getEntityName(district, ["districtName", "name", "label"]) || id,
  };
};

const getCommitteeSubtypeValue = (committee) =>
  String(committee?.committeeSubtype || committee?.committeeSubType || "")
    .trim()
    .toLowerCase();

const getCommitteeSubtypeLabel = (subtype) => {
  const value = String(subtype || "").trim();
  if (!value) return "";
  return value.toLowerCase() === "na" ? "na" : value;
};

const getCommitteeNameValue = (committee) =>
  String(committee?.name || committee?.committeeName || "").trim();

const getUniqueCommitteesByName = (committees = []) => {
  const map = new Map();
  committees.forEach((committee) => {
    const name = getCommitteeNameValue(committee);
    if (!name || map.has(name)) return;
    map.set(name, {
      ...committee,
      _id: name,
      name,
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || "")),
  );
};

const getCommitteeSubtypeOptions = (committees = []) => {
  const subtypeMap = new Map();
  committees.forEach((item) => {
    const value = getCommitteeSubtypeValue(item);
    if (!value) return;
    subtypeMap.set(value, getCommitteeSubtypeLabel(value));
  });
  return Array.from(subtypeMap, ([value, label]) => ({
    value,
    label,
  })).sort((a, b) => a.label.localeCompare(b.label));
};

const getCouncilType = (councilName = "") => {
  const value = String(councilName || "").toUpperCase();
  if (value.includes("DISTRICT EXECUTIVE COUNCIL") || value.includes("DEC")) return "DEC";
  if (value.includes("REGIONAL EXECUTIVE COUNCIL") || value.includes("REC")) return "REC";
  if (value.includes("STATE EXECUTIVE COUNCIL") || value.includes("SEC")) return "SEC";
  return "";
};

const useAssignments = ({ id, mode, path, emptyScope, showNotification }) => {
  const [councils, setCouncils] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programBaskets, setProgramBaskets] = useState([]);
  const [chapterList, setChapterList] = useState([]);
  const [scopeRegionsByState, setScopeRegionsByState] = useState({});
  const [scopeDistrictsByState, setScopeDistrictsByState] = useState({});
  const [assignmentErrors, setAssignmentErrors] = useState([]);

  const emptyCommitteeRow = {
    council: "",
    committeeState: "",
    committeeStates: [],
    councilType: "",
    committeeRegion: "",
    committeeRegions: [],
    committeeDistrict: "",
    committeeDistricts: [],
    committeeName: "",
    committeeSubtype: "",
    committeeSubtypes: [],
    committeeScopeOptions: [],
    allCommittees: [],
    committees: [],
    committee: "",
    designationOptions: [],
    designations: [],
    designationScopes: {},
    hasLoadedCommitteeOptions: false,
    isLoadingCommitteeOptions: false,
    hasLoadedDesignationOptions: false,
  };
  const [committeeRows, setCommitteeRows] = useState([emptyCommitteeRow]);
  const [authorityType, setAuthorityType] = useState([]);
  const [authorityOpen, setAuthorityOpen] = useState(false);

  const [chapterRows, setChapterRows] = useState([]);
  const emptyProgramRow = {
    basketId: "",
    programId: "",
    designation: "HEAD",
    availablePrograms: [],
    scope: { ...emptyScope },
  };
  const [programRows, setProgramRows] = useState([]);
  const [chapterRoles, setChapterRoles] = useState([]);
  const [userExistingChapterAssignments, setExistingUserChapterAssignments] =
    useState([]);
  const [openAssignmentModal, setOpenAssignmentModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [addChapterAssignments, setAddChapterAssignments] = useState(false);

  const [userAssignments, setUserAssignments] = useState([]);
  const hasHydratedAssignmentsRef = useRef(false);
  const hasUserEditedCommitteeAssignmentsRef = useRef(false);
  const initialCommitteeMembershipsRef = useRef([]);
  const scopeFetchInFlightRef = useRef(new Set());

  const clearCommitteeAssignments = () => {
    if (mode !== "create" && initialCommitteeMembershipsRef.current.length > 0) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    setCommitteeRows([{ ...emptyCommitteeRow }]);
  };

  const clearChapterAssignments = () => {
    setAddChapterAssignments(false);
    setChapterRows([]);
  };

  const clearProgramAssignments = () => {
    setProgramRows([]);
  };

  const handleAddCommitteeRow = () => {
    hasUserEditedCommitteeAssignmentsRef.current = true;
    setCommitteeRows((prev) => [...prev, { ...emptyCommitteeRow }]);
  };

  const handleRemoveCommittee = (index) => {
    hasUserEditedCommitteeAssignmentsRef.current = true;
    setCommitteeRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const hasAnyCommitteeAssignment = next.some(
        (row) => row?.committee && (row?.designations || []).length > 0,
      );
      if (!hasAnyCommitteeAssignment) {
        setAuthorityType((prevTypes) =>
          prevTypes.filter((type) => type !== "COMMITTEE"),
        );
      }
      if (next.length === 0) {
        return mode === "create" ? [{ ...emptyCommitteeRow }] : [];
      }
      return next;
    });
  };

  const handleCouncilChange = async (
    index,
    councilId,
    options = { preserveSelection: false, suppressUserEdit: false },
  ) => {
    const preserveSelection = !!options?.preserveSelection;
    const suppressUserEdit = !!options?.suppressUserEdit;
    if (!suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }

    setCommitteeRows((prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      const existingRow = updated[index];
      updated[index] = {
        ...existingRow,
        council: councilId,
        councilType: existingRow.councilType || "",
        committeeState: preserveSelection ? existingRow.committeeState || "" : "",
        committeeStates: [],
        committeeRegion: preserveSelection ? existingRow.committeeRegion || "" : "",
        committeeRegions: [],
        committeeDistrict: preserveSelection ? existingRow.committeeDistrict || "" : "",
        committeeDistricts: [],
        committeeName: preserveSelection ? existingRow.committeeName || "" : "",
        committeeSubtype: preserveSelection ? existingRow.committeeSubtype || "" : "",
        committeeSubtypes: [],
        committeeScopeOptions: [],
        allCommittees: [],
        committees: [],
        committee: preserveSelection ? existingRow.committee || "" : "",
        designationOptions: [],
        designations: preserveSelection ? existingRow.designations || [] : [],
        designationScopes: preserveSelection
          ? existingRow.designationScopes || {}
          : {},
        hasLoadedCommitteeOptions: false,
        isLoadingCommitteeOptions: Boolean(councilId),
        hasLoadedDesignationOptions: false,
      };

      return updated;
    });

    if (!councilId) {
      return;
    }

    try {
      const selectedCouncil = councils.find((c) => String(c?._id || "") === String(councilId || ""));
      const councilType = getCouncilType(selectedCouncil?.councilName || selectedCouncil?.name || "");
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/committeeDetails/committeesByCouncil`,
        { params: { councilId } },
      );

      const list = res.data.data || [];
      const hasStateBuckets = list.some((item) =>
        Array.isArray(item?.committees),
      );

      setCommitteeRows((prev) => {
        const updated = [...prev];
        const existingRow = updated[index];
        if (!existingRow) return prev;

        let nextCommitteeState =
          preserveSelection &&
          list.some((item) => String(item?._id || "") === String(existingRow.committeeState || ""))
            ? existingRow.committeeState || ""
            : "";

        if (
          hasStateBuckets &&
          preserveSelection &&
          !nextCommitteeState &&
          existingRow.committee
        ) {
          const matchedStateBucket = list.find((item) =>
            Array.isArray(item?.committees) &&
            item.committees.some(
              (committee) =>
                String(committee?._id || "") ===
                String(existingRow.committee || ""),
            ),
          );
          nextCommitteeState = matchedStateBucket?._id || "";
        }

        const committeesForSelectedState =
          hasStateBuckets
            ? list.find(
                (item) =>
                  String(item?._id || "") === String(nextCommitteeState || ""),
              )?.committees || []
            : list;

        const flattenedCommittees = hasStateBuckets
          ? list.flatMap((item) =>
              Array.isArray(item?.committees) ? item.committees : [],
            )
          : list;

        const uniqueCommittees = (Array.isArray(flattenedCommittees) ? flattenedCommittees : []).reduce(
          (acc, committee) => {
            const keyByName = String(
              committee?.name || committee?.committeeName || "",
            )
              .trim()
              .toLowerCase();
            const keyById = String(committee?._id || "").trim().toLowerCase();
            const key = keyByName || keyById;
            if (!key) return acc;

            const existingIndex = acc.findIndex((item) => {
              const itemName = String(
                item?.name || item?.committeeName || "",
              )
                .trim()
                .toLowerCase();
              const itemId = String(item?._id || "").trim().toLowerCase();
              return (itemName || itemId) === key;
            });

            if (existingIndex === -1) {
              acc.push(committee);
              return acc;
            }

            const isCurrentSelection =
              String(committee?._id || "") === String(existingRow?.committee || "");
            if (isCurrentSelection) {
              acc[existingIndex] = committee;
            }
            return acc;
          },
          [],
        );

        const committeesForScopeFiltering = ["REC", "DEC"].includes(councilType)
          ? flattenedCommittees
          : uniqueCommittees;

        const derivedStates = [];
        let committeeRegions = [];
        let committeeDistricts = [];
        let committeeSubtypes = [];
        let committeeScopeOptions = [];
        let nextCommitteeRegion = preserveSelection ? existingRow.committeeRegion || "" : "";
        let nextCommitteeDistrict = preserveSelection ? existingRow.committeeDistrict || "" : "";
        let nextCommitteeName = preserveSelection ? existingRow.committeeName || "" : "";
        let nextCommitteeSubtype = preserveSelection ? existingRow.committeeSubtype || "" : "";
        let filteredCommittees = hasStateBuckets
          ? committeesForSelectedState
          : committeesForScopeFiltering;

        if (!hasStateBuckets && (councilType === "REC" || councilType === "DEC")) {
          const stateMap = new Map();
          committeesForScopeFiltering.forEach((item) => {
            const id = getEntityId(item, "stateId", "state");
            if (!id) return;
            const name =
              getEntityName(item?.state, ["stateName", "name", "label"]) ||
              getEntityName(item?.stateName, ["stateName", "name", "label"]) ||
              id;
            stateMap.set(String(id), { _id: id, stateName: name });
          });
          const states = Array.from(stateMap.values());
          derivedStates.push(...states);
          if (!nextCommitteeState && preserveSelection && existingRow.committee) {
            const selectedCommittee = committeesForScopeFiltering.find(
              (item) => String(item?._id || "") === String(existingRow.committee || ""),
            );
            nextCommitteeState = getEntityId(selectedCommittee, "stateId", "state");
            nextCommitteeRegion = getEntityId(selectedCommittee, "regionId", "region");
            nextCommitteeDistrict = getEntityId(selectedCommittee, "districtId", "district");
            nextCommitteeName = getCommitteeNameValue(selectedCommittee);
            nextCommitteeSubtype = getCommitteeSubtypeValue(selectedCommittee);
          }
          const filteredByState = nextCommitteeState
            ? committeesForScopeFiltering.filter(
                (item) =>
                  String(getEntityId(item, "stateId", "state")) ===
                  String(nextCommitteeState || ""),
              )
            : [];
          const regionMap = new Map();
            filteredByState.forEach((item) => {
              const id = getEntityId(item, "regionId", "region");
              if (!id) return;
              const name =
                getEntityName(item?.region, ["regionName", "name", "label"]) ||
                getEntityName(item?.regionName, ["regionName", "name", "label"]) ||
                id;
              regionMap.set(String(id), { _id: id, regionName: name });
            });
          committeeRegions = Array.from(regionMap.values());
          const filteredByStateAndRegion = nextCommitteeRegion
            ? filteredByState.filter(
                (item) =>
                  String(getEntityId(item, "regionId", "region")) ===
                  String(nextCommitteeRegion || ""),
              )
            : filteredByState;
          const filteredByFullScope = nextCommitteeDistrict
            ? filteredByStateAndRegion.filter(
                (item) =>
                  String(getEntityId(item, "districtId", "district")) ===
                  String(nextCommitteeDistrict || ""),
              )
            : filteredByStateAndRegion;
          filteredCommittees = preserveSelection
            ? filteredByFullScope
            : filteredByState;

          if (councilType === "DEC") {
            const districtMap = new Map();
            filteredByState.forEach((item) => {
              const id = getEntityId(item, "districtId", "district");
              if (!id) return;
              const name =
                getEntityName(item?.district, ["districtName", "name", "label"]) ||
                getEntityName(item?.districtName, ["districtName", "name", "label"]) ||
                id;
              const regionId = getEntityId(item, "regionId", "region");
              districtMap.set(String(id), {
                _id: id,
                districtName: name,
                regionId,
              });
            });
            committeeDistricts = Array.from(districtMap.values());

            committeeScopeOptions = filteredByFullScope;
            filteredCommittees = getUniqueCommitteesByName(filteredByFullScope);

            if (!nextCommitteeName && preserveSelection && existingRow.committee) {
              const selectedCommittee = filteredByFullScope.find(
                (item) => String(item?._id || "") === String(existingRow.committee || ""),
              );
              nextCommitteeName = getCommitteeNameValue(selectedCommittee);
            }

            const selectedNameMatches = nextCommitteeName
              ? filteredByFullScope.filter(
                  (item) => getCommitteeNameValue(item) === nextCommitteeName,
                )
              : [];

            committeeSubtypes = getCommitteeSubtypeOptions(selectedNameMatches);

            if (
              nextCommitteeSubtype &&
              !committeeSubtypes.some((item) => item.value === nextCommitteeSubtype)
            ) {
              nextCommitteeSubtype = "";
            }

            if (nextCommitteeSubtype) {
              const selectedCommittee = selectedNameMatches.find(
                (item) => getCommitteeSubtypeValue(item) === nextCommitteeSubtype,
              );
              if (selectedCommittee) {
                filteredCommittees = getUniqueCommitteesByName(filteredByFullScope);
              }
            } else if (committeeSubtypes.length === 1) {
              nextCommitteeSubtype = committeeSubtypes[0].value;
              const selectedCommittee = selectedNameMatches.find(
                (item) => getCommitteeSubtypeValue(item) === nextCommitteeSubtype,
              );
              if (selectedCommittee) {
                filteredCommittees = getUniqueCommitteesByName(filteredByFullScope);
              }
            }
          }
        }

        updated[index] = {
          ...existingRow,
          councilType,
          committeeStates: hasStateBuckets
            ? list.map(normalizeStateOption).filter(Boolean)
            : derivedStates,
          committeeState: ["REC", "DEC"].includes(councilType)
            ? nextCommitteeState
            : hasStateBuckets
              ? nextCommitteeState
              : "",
          committeeRegion: ["REC", "DEC"].includes(councilType)
            ? nextCommitteeRegion
            : "",
          committeeDistrict: councilType === "DEC" ? nextCommitteeDistrict : "",
          committeeName: councilType === "DEC" ? nextCommitteeName : "",
          committeeSubtype: councilType === "DEC" ? nextCommitteeSubtype : "",
          allCommittees: committeesForScopeFiltering,
          committees: filteredCommittees,
          committeeRegions,
          committeeDistricts,
          committeeSubtypes,
          committeeScopeOptions,
          hasLoadedCommitteeOptions: true,
          isLoadingCommitteeOptions: false,
        };

        return updated;
      });
    } catch (err) {
      console.error(err);
      setCommitteeRows((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = {
          ...updated[index],
          isLoadingCommitteeOptions: false,
        };
        return updated;
      });
    }
  };

  const handleCommitteeStateChange = (
    index,
    stateId,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    const updated = [...committeeRows];
    updated[index].committeeState = stateId;
    updated[index].committeeRegion = "";
    updated[index].committeeRegions = [];
    updated[index].committeeDistrict = "";
    updated[index].committeeDistricts = [];
    updated[index].committeeName = "";
    updated[index].committeeSubtype = "";
    updated[index].committeeSubtypes = [];
    updated[index].committeeScopeOptions = [];
    updated[index].committee = "";
    updated[index].designations = [];
    updated[index].designationOptions = [];
    updated[index].designationScopes = {};

    const selectedState = updated[index].committeeStates.find(
      (s) => String(s?._id || "") === String(stateId || ""),
    );
    const rowCouncilType = updated[index].councilType || "";
    if (rowCouncilType === "REC" || rowCouncilType === "DEC") {
      const allCommittees = updated[index].allCommittees || [];
      const filteredByState = allCommittees.filter(
        (item) =>
          String(getEntityId(item, "stateId", "state")) === String(stateId || ""),
      );
      const masterRegions = Array.isArray(scopeRegionsByState?.[stateId])
        ? scopeRegionsByState[stateId]
        : [];
      updated[index].committeeRegions = masterRegions
        .map(normalizeRegionOption)
        .filter(Boolean);
      updated[index].committeeDistricts = [];
      updated[index].committees = rowCouncilType === "REC" ? [] : filteredByState;
    } else {
      updated[index].committees = selectedState?.committees || [];
    }

    setCommitteeRows(updated);
    if (stateId) {
      fetchScopeRegionsAndDistricts(stateId);
    }
  };

  const handleCommitteeRegionChange = (
    index,
    regionId,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    const updated = [...committeeRows];
    const row = updated[index];
    if (!row) return;

    row.committeeRegion = regionId;
    row.committeeDistrict = "";
    row.committeeName = "";
    row.committeeSubtype = "";
    row.committeeSubtypes = [];
    row.committeeScopeOptions = [];
    row.committee = "";
    row.designations = [];
    row.designationOptions = [];
    row.designationScopes = {};

    const filteredByStateAndRegion = (row.allCommittees || []).filter(
      (item) =>
        String(getEntityId(item, "stateId", "state")) === String(row.committeeState || "") &&
        String(getEntityId(item, "regionId", "region")) === String(regionId || ""),
    );
    const isDec = row.councilType === "DEC";
    if (isDec) {
      const masterDistricts = Array.isArray(scopeDistrictsByState?.[row.committeeState])
        ? scopeDistrictsByState[row.committeeState]
        : [];
      row.committeeDistricts = masterDistricts
        .map(normalizeDistrictOption)
        .filter(Boolean);
      row.committees = [];
    } else {
      row.committeeDistricts = [];
      row.committees = filteredByStateAndRegion;
    }

    setCommitteeRows(updated);
  };

  const handleCommitteeDistrictChange = (
    index,
    districtId,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    const updated = [...committeeRows];
    const row = updated[index];
    if (!row) return;

    row.committeeDistrict = districtId;
    row.committeeName = "";
    row.committeeSubtype = "";
    row.committeeSubtypes = [];
    row.committeeScopeOptions = [];
    row.committee = "";
    row.designations = [];
    row.designationOptions = [];
    row.designationScopes = {};

    const filteredByScope = (row.allCommittees || []).filter(
      (item) =>
        String(getEntityId(item, "stateId", "state")) === String(row.committeeState || "") &&
        String(getEntityId(item, "regionId", "region")) === String(row.committeeRegion || "") &&
        String(getEntityId(item, "districtId", "district")) === String(districtId || ""),
    );

    if (row.councilType === "DEC") {
      row.committeeScopeOptions = filteredByScope;
      row.committees = getUniqueCommitteesByName(filteredByScope);
    } else {
      row.committees = filteredByScope;
    }

    setCommitteeRows(updated);
  };

  const normalizeCommitteeMembershipRows = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
      .filter((row) => row?.committee)
      .map((row) => ({
        committee: String(row?.committee || ""),
        designations: (Array.isArray(row?.designations) ? row.designations : [])
          .map((designation) => String(designation || ""))
          .filter(Boolean)
          .sort(),
      }))
      .sort((a, b) => {
        const ax = `${a.committee}|${a.designations.join(",")}`;
        const ay = `${b.committee}|${b.designations.join(",")}`;
        return ax.localeCompare(ay);
      });

  const recordInitialCommitteeMemberships = (rows = []) => {
    initialCommitteeMembershipsRef.current = normalizeCommitteeMembershipRows(rows);
    hasUserEditedCommitteeAssignmentsRef.current = false;
  };

  const handleCommitteeSubtypeChange = (
    index,
    subtype,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    const updated = [...committeeRows];
    const row = updated[index];
    if (!row) return;

    row.committeeSubtype = subtype;
    row.committee = "";
    row.designations = [];
    row.designationOptions = [];
    row.designationScopes = {};

    const selectedCommittee = (row.committeeScopeOptions || []).find(
      (item) =>
        String(getEntityId(item, "stateId", "state")) === String(row.committeeState || "") &&
        String(getEntityId(item, "regionId", "region")) === String(row.committeeRegion || "") &&
        String(getEntityId(item, "districtId", "district")) === String(row.committeeDistrict || "") &&
        getCommitteeNameValue(item) === String(row.committeeName || "") &&
        getCommitteeSubtypeValue(item) === String(subtype || ""),
    );

    if (selectedCommittee) {
      row.committee = selectedCommittee._id;
      row.committees = getUniqueCommitteesByName(row.committeeScopeOptions || []);
      row.designationOptions =
        selectedCommittee?.designations?.map((d) => ({
          _id: d._id,
          title: d.title || d.label || d.name || "",
          label: d.label || d.title || d.name || "",
          policyRole: getDesignationPolicyRole(d),
          defaultPrivileges: normalizePrivileges(d.defaultPrivileges),
          privileges: normalizePrivileges(d.privileges),
        })) || [];
      row.hasLoadedDesignationOptions = true;
    } else {
      row.committees = getUniqueCommitteesByName(row.committeeScopeOptions || []);
      row.hasLoadedDesignationOptions = false;
    }

    setCommitteeRows(updated);
  };
  const handleCommitteeChange = (
    index,
    committeeId,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    setCommitteeRows((prevRows) => {
      const updated = [...prevRows];
      if (!updated[index]) return prevRows;

      if (updated[index].councilType === "DEC") {
        const selectedById = (updated[index].committeeScopeOptions || []).find(
          (item) => String(item?._id || "") === String(committeeId || ""),
        );

        if (selectedById) {
          const committeeName = getCommitteeNameValue(selectedById);
          const committeeMatches = (updated[index].committeeScopeOptions || []).filter(
            (item) => getCommitteeNameValue(item) === committeeName,
          );
          const subtypeOptions = getCommitteeSubtypeOptions(committeeMatches);

          updated[index].committeeName = committeeName;
          updated[index].committeeSubtype = getCommitteeSubtypeValue(selectedById);
          updated[index].committeeSubtypes = subtypeOptions;
          updated[index].committee = selectedById._id;
          updated[index].committees = getUniqueCommitteesByName(
            updated[index].committeeScopeOptions || [],
          );
          updated[index].designationOptions =
            selectedById?.designations?.map((d) => ({
              _id: d._id,
              title: d.title || d.label || d.name || "",
              label: d.label || d.title || d.name || "",
              policyRole: getDesignationPolicyRole(d),
              defaultPrivileges: normalizePrivileges(d.defaultPrivileges),
              privileges: normalizePrivileges(d.privileges),
            })) || [];
          updated[index].hasLoadedDesignationOptions = true;

          if (!updated[index].designations || updated[index].designations.length === 0) {
            updated[index].designations = [];
            updated[index].designationScopes = {};
          }

          return updated;
        }

        const committeeName = committeeId;
        const committeeMatches = (updated[index].committeeScopeOptions || []).filter(
          (item) => getCommitteeNameValue(item) === String(committeeName || ""),
        );
        const subtypeOptions = getCommitteeSubtypeOptions(committeeMatches);

        updated[index].committeeName = committeeName;
        updated[index].committeeSubtype = "";
        updated[index].committeeSubtypes = subtypeOptions;
        updated[index].committee = "";
        updated[index].designationOptions = [];
        updated[index].designations = [];
        updated[index].designationScopes = {};
        updated[index].hasLoadedDesignationOptions = false;

        if (subtypeOptions.length === 1) {
          const selectedCommittee = committeeMatches.find(
            (item) => getCommitteeSubtypeValue(item) === subtypeOptions[0].value,
          );
          if (selectedCommittee) {
            updated[index].committeeSubtype = subtypeOptions[0].value;
            updated[index].committee = selectedCommittee._id;
            updated[index].committees = getUniqueCommitteesByName(
              updated[index].committeeScopeOptions || [],
            );
            updated[index].designationOptions =
              selectedCommittee?.designations?.map((d) => ({
                _id: d._id,
                title: d.title || d.label || d.name || "",
                label: d.label || d.title || d.name || "",
                policyRole: getDesignationPolicyRole(d),
                defaultPrivileges: normalizePrivileges(d.defaultPrivileges),
                privileges: normalizePrivileges(d.privileges),
              })) || [];
            updated[index].hasLoadedDesignationOptions = true;
          }
        }

        return updated;
      }

      updated[index].committee = committeeId;

      const selectedCommittee = updated[index].committees.find(
        (c) => c._id === committeeId,
      );

      if (committeeId && !selectedCommittee) {
        // Committee options may still be settling; do not wipe existing
        // designations/scopes during this transient state.
        return updated;
      }

      const newDesignationOptions =
        selectedCommittee?.designations?.map((d) => ({
          _id: d._id,
          title: d.title || d.label || d.name || "",
          label: d.label || d.title || d.name || "",
          policyRole: getDesignationPolicyRole(d),
          defaultPrivileges: normalizePrivileges(d.defaultPrivileges),
          privileges: normalizePrivileges(d.privileges),
        })) || [];

      updated[index].designationOptions = newDesignationOptions;
      updated[index].hasLoadedDesignationOptions = true;

      if (!updated[index].designations || updated[index].designations.length === 0) {
        updated[index].designations = [];
        updated[index].designationScopes = {};
      } else {
        if (!newDesignationOptions.length) {
          return updated;
        }
        const normalizedOptionIds = new Set(
          newDesignationOptions.map((d) => normalizeIdToken(d?._id)),
        );

        const previousScopes = { ...(updated[index].designationScopes || {}) };
        const nextScopes = {};

        updated[index].designations = updated[index].designations.filter((d) => {
          const token = normalizeIdToken(d);
          if (!token || !normalizedOptionIds.has(token)) return false;

          const exactScope = previousScopes[d];
          const tokenScope = getScopeByDesignationId(previousScopes, d);
          if (exactScope) {
            nextScopes[d] = exactScope;
          } else if (tokenScope) {
            nextScopes[d] = tokenScope;
          }
          return true;
        });

        updated[index].designationScopes = nextScopes;
      }

      return updated;
    });
  };

  const handleDesignationChange = (
    index,
    values,
    options = { suppressUserEdit: false },
  ) => {
    if (!options?.suppressUserEdit) {
      hasUserEditedCommitteeAssignmentsRef.current = true;
    }
    const nextValues = Array.isArray(values) ? values : [values];
    setCommitteeRows((prevRows) => {
      const updated = [...prevRows];
      if (!updated[index]) return prevRows;

      updated[index].designations = nextValues;
      updated[index].designationScopes = updated[index].designationScopes || {};

      nextValues.forEach((designationId) => {
        if (!updated[index].designationScopes[designationId]) {
          updated[index].designationScopes[designationId] = {
            level: "NATIONAL",
            state: "",
            region: "",
            district: "",
          };
        }
      });

      Object.keys(updated[index].designationScopes || {}).forEach(
        (existingId) => {
          if (!nextValues.includes(existingId)) {
            delete updated[index].designationScopes[existingId];
          }
        },
      );

      return updated;
    });
  };

  const fetchScopeRegionsAndDistricts = async (stateId) => {
    if (!stateId) return;
    try {
      const [regionRes, districtRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_URL}/state/${stateId}/region`),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/state/${stateId}/district`,
        ),
      ]);
      const regionsData = regionRes?.data?.data ?? regionRes?.data ?? [];
      const districtsData = districtRes?.data?.data ?? districtRes?.data ?? [];
      setScopeRegionsByState((prev) => ({
        ...prev,
        [stateId]: Array.isArray(regionsData) ? regionsData : [],
      }));
      setScopeDistrictsByState((prev) => ({
        ...prev,
        [stateId]: Array.isArray(districtsData) ? districtsData : [],
      }));
    } catch (err) {
      console.error("Error fetching scope regions/districts:", err);
    }
  };

  const handleScopeChange = (rowIndex, designationId, field, value) => {
    hasUserEditedCommitteeAssignmentsRef.current = true;
    setCommitteeRows((prevRows) => {
      const updated = [...prevRows];
      if (!updated[rowIndex]) return prevRows;

      if (!updated[rowIndex].designationScopes) {
        updated[rowIndex].designationScopes = {};
      }

      if (!updated[rowIndex].designationScopes[designationId]) {
        updated[rowIndex].designationScopes[designationId] = { ...emptyScope };
      }

      if (field === "level") {
        const base = { ...emptyScope, level: value };
        updated[rowIndex].designationScopes[designationId] = base;
      } else {
        updated[rowIndex].designationScopes[designationId][field] = value;
      }

      return updated;
    });
    if (field === "state" && value) {
      fetchScopeRegionsAndDistricts(value);
    }
  };

  const getChapterScopeLevel = (row) => {
    if (row.villageId) return "VILLAGE";
    if (row.talukaId) return "TALUKA";
    if (row.districtId) return "DISTRICT";
    if (row.regionId) return "REGION";
    if (row.stateId) return "STATE";
    return "NATIONAL";
  };

  const getDefaultChapterScope = (row = {}) => ({
    level: getChapterScopeLevel(row),
    state: row.stateId || "",
    region: row.regionId || "",
    district: row.districtId || "",
    taluka: row.talukaId || "",
    chapter: row._id || "",
  });

  const fetchProgramBaskets = useCallback(async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/programBaskets`,
      );
      const baskets = normalizeArray(res);
      setProgramBaskets(baskets);
      const flatPrograms = baskets.flatMap((b) =>
        Array.isArray(b.programs) ? b.programs : [],
      );
      setPrograms(flatPrograms);
    } catch (err) {
      console.error("Error fetching program baskets:", err);
      setProgramBaskets([]);
    }
  }, []);

  const handleAddProgramRow = () => {
    setProgramRows((prev) => [...prev, { ...emptyProgramRow }]);
  };

  const handleRemoveProgramRow = (index) => {
    setProgramRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProgramBasketChange = async (index, basketId) => {
    const selectedBasket = programBaskets.find(
      (b) => String(b._id || "") === String(basketId || "") || String(b.basketId || "") === String(basketId || ""),
    );
    const normalizedBasketId = selectedBasket?.basketId || basketId;
    const normalizedBasketMongoId = selectedBasket?._id || basketId;
    const nextRows = [...programRows];
    nextRows[index] = {
      ...nextRows[index],
      basketId: normalizedBasketId,
      basketMongoId: normalizedBasketMongoId,
      programId: "",
      designation: "HEAD",
      availablePrograms: [],
      scope: nextRows[index]?.scope || { ...emptyScope },
    };

    if (!normalizedBasketId) {
      setProgramRows(nextRows);
      return;
    }

    try {

      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/programs/baskets/${normalizedBasketId}`,
      );
      nextRows[index].availablePrograms = extractProgramsFromBasket(res);
    } catch (err) {
      console.error("Error fetching basket programs:", err);
      nextRows[index].availablePrograms = [];
    }

    setProgramRows(nextRows);
  };

  const handleProgramSelectionChange = (index, programId) => {
    setProgramRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], programId };
      return next;
    });
  };

  const handleProgramScopeChange = (index, field, value) => {
    setProgramRows((prev) => {
      const next = [...prev];
      const currentScope = next[index]?.scope || { ...emptyScope };
      let updatedScope = { ...currentScope };

      if (field === "level") {
        updatedScope = { ...emptyScope, level: value };
      } else {
        updatedScope[field] = value;
      }

      next[index] = { ...next[index], scope: updatedScope };
      return next;
    });

    if (field === "state" && value) {
      fetchScopeRegionsAndDistricts(value);
    }
  };

  const handleChapterScopeChange = (chapterId, field, value) => {
    setChapterRows((prevRows) =>
      prevRows.map((row) => {
        if (row._id !== chapterId) return row;

        const currentScope = row.scope || getDefaultChapterScope(row);
        let updatedScope = { ...currentScope };

        if (field === "level") {
          updatedScope = {
            ...emptyScope,
            taluka: "",
            chapter: row._id,
            level: value,
          };
        } else if (field === "state") {
          updatedScope.state = value;
          updatedScope.region = "";
          updatedScope.district = "";
          updatedScope.taluka = "";
        } else if (field === "region") {
          updatedScope.region = value;
          updatedScope.district = "";
          updatedScope.taluka = "";
        } else if (field === "district") {
          updatedScope.district = value;
          updatedScope.taluka = "";
        } else {
          updatedScope[field] = value;
        }

        return { ...row, scope: updatedScope };
      }),
    );

    if (field === "state" && value) {
      fetchScopeRegionsAndDistricts(value);
    }
  };

  const buildUserAssignments = (userId) => {
    const assignments = [];

    committeeRows.forEach((row) => {
      row.designations.forEach((designationId) => {
        const designationOption = row.designationOptions.find(
          (d) => String(d?._id || "") === String(designationId || ""),
        );
        const scope = getScopeByDesignationId(
          row.designationScopes,
          designationId,
        );

        if (!scope) return;
        const normalizedLevel = String(scope.level || "NATIONAL")
          .trim()
          .toUpperCase();
        if (!normalizedLevel) return;

        const designationPayload = {
          label: getDesignationLabel(designationOption),
          policyRole: getDesignationPolicyRole(designationOption) || undefined,
        };

        const privilegesPayload = getDesignationPrivileges(designationOption);

        const assignment = {
          user: userId,
          sourceType: "COMMITTEE",
          sourceRef: row.committee,
          designation: designationPayload,
          scope: {
            level: normalizedLevel,
            state: scope.state || null,
            region: scope.region || null,
            district: scope.district || null,
            chapter: scope.chapter || null,
            program: null,
          },
        };

        if (privilegesPayload) {
          assignment.privileges = privilegesPayload;
        }

        assignments.push(assignment);
      });
    });

    chapterRows.forEach((row) => {
      if (!row.isAssigned || !row.designation) return;
      const roleLabel =
        CHAPTER_ROLE_OPTIONS.find((r) => r.value === row.designation)?.label ||
        row.designation;
      const scope = row.scope || getDefaultChapterScope(row);
      const level = String(scope.level || "NATIONAL")
        .trim()
        .toUpperCase();

      assignments.push({
        user: userId,
        sourceType: "CHAPTER",
        sourceRef: row._id,
        designation: roleLabel,
        scope: {
          level,
          state: scope.state || null,
          region: scope.region || null,
          district: scope.district || null,
          taluka: scope.taluka || null,
          village: row.villageId || null,
          chapter: row._id,
          program: null,
        },
      });
    });

    programRows.forEach((row) => {
      if (!row.basketId && !row.basketMongoId) return;
      const scope = row.scope || {};

      assignments.push({
        user: userId,
        sourceType: "PROGRAM",
        sourceRef: row.basketMongoId || row.basketId,
        designation: "HEAD",
        scope: {
          level: scope.level || "NATIONAL",
          state: scope.state || null,
          region: scope.region || null,
          district: scope.district || null,
          taluka: null,
          village: null,
          chapter: scope.chapter || null,
          program: row.programId || null,
          basket: row.basketId,
        },
      });
    });

    return assignments;
  };

  const runAssignments = async (userId) => {
    const errors = [];

    const hadCommitteeAssignments = (userAssignments || []).some(
      (assignment) => assignment?.sourceType === "COMMITTEE",
    );
    const hadInitialCommitteeMemberships =
      initialCommitteeMembershipsRef.current.length > 0;
    const shouldSyncCommitteeMembership =
      authorityType.includes("COMMITTEE") ||
      hadCommitteeAssignments ||
      hadInitialCommitteeMemberships;

    if (shouldSyncCommitteeMembership) {
      const committees = committeeRows
        .filter((row) => row?.committee)
        .map((row) => ({
          committeeId: row.committee,
          designations: Array.isArray(row.designations) ? row.designations : [],
        }));

      const payload = { userId, committees };

      try {
        if (
          mode === "create" &&
          (path === "/registerNewUser" ||
            path === "/committee/registerNewUser")
        ) {
          await ApiCallService.post(
            `${process.env.REACT_APP_BASE_URL}/committeeMembers/addMember`,
            payload,
          );
        } else {
          await ApiCallService.put(
            `${process.env.REACT_APP_BASE_URL}/committeeMembers/updateMember/${userId}`,
            payload,
          );
        }
      } catch (err) {
        errors.push(
          err?.response?.data?.message ||
            err?.message ||
            "Committee assignment failed",
        );
      }
    }

    for (const type of authorityType) {
      if (type === "CHAPTER") {
        const calls = [];

        chapterRows.forEach((row) => {
          const wasAssigned = !!row.existingAssignment;
          const isAssigned = !!row.isAssigned;
          const existingRoleId = row.existingAssignment?.roleId || "";
          const newRoleId = row.designation || "";

          // Only make API calls for actual changes
          if (isAssigned && !wasAssigned) {
            // New assignment
            if (!newRoleId) return;
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/assign`,
                { chapterId: row._id, roleId: newRoleId },
              ),
            );
          } else if (!isAssigned && wasAssigned) {
            // Unassignment
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/unassign`,
                { chapterId: row._id },
              ),
            );
          } else if (isAssigned && wasAssigned && existingRoleId !== newRoleId) {
            // Role change for existing assignment
            if (!newRoleId) return;
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/assign`,
                { chapterId: row._id, roleId: newRoleId },
              ),
            );
          }
          // If isAssigned && wasAssigned && existingRoleId === newRoleId, no change needed
        });

        if (calls.length > 0) {
          const results = await Promise.allSettled(calls);
          results.forEach((r) => {
            if (r.status === "rejected") {
              const msg =
                r.reason?.response?.data?.message ||
                r.reason?.message ||
                "Chapter assignment failed";
              errors.push(msg);
            }
          });
        }
      }
    }

    if (!authorityType.includes("CHAPTER")) {
      const hasChapterChanges = chapterRows.some(
        (row) => row.isAssigned !== !!row.existingAssignment ||
                 (row.isAssigned && row.existingAssignment && row.designation !== row.existingAssignment.roleId)
      );
      if (hasChapterChanges) {
        const calls = [];

        chapterRows.forEach((row) => {
          const wasAssigned = !!row.existingAssignment;
          const isAssigned = !!row.isAssigned;
          const existingRoleId = row.existingAssignment?.roleId || "";
          const newRoleId = row.designation || "";

          // Only make API calls for actual changes
          if (isAssigned && !wasAssigned) {
            // New assignment
            if (!newRoleId) return;
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/assign`,
                { chapterId: row._id, roleId: newRoleId },
              ),
            );
          } else if (!isAssigned && wasAssigned) {
            // Unassignment
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/unassign`,
                { chapterId: row._id },
              ),
            );
          } else if (isAssigned && wasAssigned && existingRoleId !== newRoleId) {
            // Role change for existing assignment
            if (!newRoleId) return;
            calls.push(
              ApiCallService.post(
                `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}/assign`,
                { chapterId: row._id, roleId: newRoleId },
              ),
            );
          }
          // If isAssigned && wasAssigned && existingRoleId === newRoleId, no change needed
        });

        if (calls.length > 0) {
          const results = await Promise.allSettled(calls);
          results.forEach((r) => {
            if (r.status === "rejected") {
              const msg =
                r.reason?.response?.data?.message ||
                r.reason?.message ||
                "Chapter assignment failed";
              errors.push(msg);
            }
          });
        }
      }
    }

    return errors;
  };

  const syncUserAssignments = async (userId) => {
    const assignmentsPayload = buildUserAssignments(userId);

    try {
      await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/userAssignments/replace/${userId}`,
        { assignments: assignmentsPayload },
      );
      return null;
    } catch (err) {
      return (
        err?.response?.data?.message ||
        err?.message ||
        "User assignments failed"
      );
    }
  };

  const fetchChapters = useCallback(async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/getAllChapters`,
      );
      const data = res?.data ?? res ?? [];
      setChapterList(Array.isArray(data) ? data : []);
    } catch (er) {
      console.error("Error Found : ", er);
    }
  }, []);

  const fetchChapterRoles = useCallback(async () => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/chapter-members/chapterRoles`,
      );
      const data = res?.data ?? res ?? [];
      setChapterRoles(Array.isArray(data) ? data : []);
    } catch (er) {
      console.error("Error Found : ", er);
    }
  }, []);

  const CHAPTER_ROLE_OPTIONS = chapterRoles.map((r) => ({
    value: r._id,
    label: r.name,
    code: r.code,
  }));

  const fetchUserChapterAssignments = async (userId) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/chapter-members/user/${userId}`,
      );
      const data = res?.data?.data ?? res?.data ?? res ?? [];
      setExistingUserChapterAssignments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("Unable to fetch user chapter assignments:", err);
      setExistingUserChapterAssignments([]);
    }
  };

  useEffect(() => {
    if (!id || mode === "create") return;
    fetchUserChapterAssignments(id);
  }, [id, mode]);

  useEffect(() => {
    if (!chapterList.length) return;

    const assignedMap = new Map(
      userExistingChapterAssignments.map((a) => [a.chapterId, a]),
    );

    const assignedRows = [];
    const unassignedRows = [];

    chapterList.forEach((ch) => {
      const existing = assignedMap.get(ch._id);

      const row = {
        _id: ch._id,
        chapterName: ch.chapterName,
        stateName: ch.location?.state?.stateName || "-",
        districtName: ch.location?.district?.districtName || "-",
        chapterLifecycleStatus: ch.chapterLifecycleStatus,
        stateId: ch.location?.state?._id || null,
        regionId: ch.location?.region?._id || null,
        districtId: ch.location?.district?._id || null,
        talukaId: ch.location?.taluka?._id || null,
        villageId: ch.location?.village?._id || null,
        isAssigned: !!existing,
        designation: existing?.roleId || "",
        existingAssignment: existing,
        scope: getDefaultChapterScope({
          _id: ch._id,
          stateId: ch.location?.state?._id || null,
          regionId: ch.location?.region?._id || null,
          districtId: ch.location?.district?._id || null,
          talukaId: ch.location?.taluka?._id || null,
          villageId: ch.location?.village?._id || null,
        }),
      };

      if (existing) {
        assignedRows.push(row);
      } else {
        unassignedRows.push({
          ...row,
          isAssigned: false,
          designation: "",
        });
      }
    });

    setChapterRows([...assignedRows, ...unassignedRows]);
  }, [chapterList, userExistingChapterAssignments]);

  const fetchRoleAvailability = async (chapterId) => {
    try {
      const res = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/chapter-members/role-availability/${chapterId}`,
      );
      return res || {};
    } catch (err) {
      console.warn("Unable to fetch role availability:", err);
      return {};
    }
  };

  const toggleAssign = (chapterId) => {
    setChapterRows((prev) =>
      prev.map((r) =>
        r._id === chapterId ? { ...r, isAssigned: !r.isAssigned } : r,
      ),
    );
  };

  const handleAssignChapter = async (chapterId) => {
    const row = chapterRows.find((r) => r._id === chapterId);
    if (!row) return;

    if (!row.designation) {
      showNotification("Please select designation before assigning", "error");
      return;
    }

    const selectedRole = CHAPTER_ROLE_OPTIONS.find(
      (r) => r.value === row.designation,
    );

    if (selectedRole?.code === "CHAPTER_MEMBER") {
      toggleAssign(chapterId);
      return;
    }

    const availability = await fetchRoleAvailability(chapterId);
    const roleAvailability = availability?.[selectedRole?.code];

    if (roleAvailability && !roleAvailability.available) {
      showNotification(
        `${selectedRole.label} is already assigned to ${roleAvailability.assignedTo.name}`,
        "error",
      );

      setChapterRows((prev) =>
        prev.map((r) => (r._id === chapterId ? { ...r, designation: "" } : r)),
      );
      return;
    }

    toggleAssign(chapterId);
  };

  const handleChapterDesignationChange = (chapterId, designation) => {
    setChapterRows((prevRows) =>
      prevRows.map((row) =>
        row._id === chapterId ? { ...row, designation } : row,
      ),
    );
  };

  useEffect(() => {
    fetchProgramBaskets();
    
    fetchChapters();
    fetchChapterRoles();
  }, [fetchChapters, fetchChapterRoles, fetchProgramBaskets, mode]);

  const handleAddChapterAssignments = () => {
    setAddChapterAssignments(true);
    if (chapterRows.length === 0 && chapterList.length > 0) {
      const rows = chapterList.map((ch) => {
        const existing = userExistingChapterAssignments.find(
          (a) => a.chapterId === ch._id,
        );

        return {
          _id: ch._id,
          chapterName: ch.chapterName,
          stateName: ch.location?.state?.stateName || "-",
          districtName: ch.location?.district?.districtName || "-",
          chapterLifecycleStatus: ch.chapterLifecycleStatus,
          stateId: ch.location?.state?._id || null,
          regionId: ch.location?.region?._id || null,
          districtId: ch.location?.district?._id || null,
          talukaId: ch.location?.taluka?._id || null,
          villageId: ch.location?.village?._id || null,
          isAssigned: !!existing,
          designation: existing?.roleId || "",
          existingAssignment: existing || null,
          roleAvailability: {},
          scope: getDefaultChapterScope({
            _id: ch._id,
            stateId: ch.location?.state?._id || null,
            regionId: ch.location?.region?._id || null,
            districtId: ch.location?.district?._id || null,
            talukaId: ch.location?.taluka?._id || null,
            villageId: ch.location?.village?._id || null,
          }),
        };
      });
      setChapterRows(rows);
    }
  };

  const roleNameByCode = Object.fromEntries(
    chapterRoles.map((r) => [r.code, r.name]),
  );

  const EXISTING_ASSIGNMENT_ROWS = userExistingChapterAssignments.map((a) => ({
    chapterId: a.chapterId,
    chapterName: a.chapterName,
    chapterStatus: a.chapterLifecycleStatus || "-",
    stateName: a.chapterLocation?.state?.stateName || "-",
    districtName: a.chapterLocation?.state?.districtName || "-",
    roleName: roleNameByCode[a.roleCode] || a.roleCode,
  }));

  const EXISTING_ASSIGNMENT_COLUMNS = [
    { field: "chapterName", headerName: "Chapter" },
    { field: "stateName", headerName: "State" },
    { field: "districtName", headerName: "District" },
    { field: "chapterStatus", headerName: "Status" },
    { field: "roleName", headerName: "Role" },
  ];

  const TESTcolumns = [
    {
      field: "chapterName",
      headerName: "Chapter Name",
    },
    {
      field: "stateName",
      headerName: "State",
      disableSort: true,
    },
    {
      field: "districtName",
      headerName: "District",
      disableSort: true,
    },
    {
      field: "chapterLifecycleStatus",
      headerName: "Chapter Status",
      disableSort: true,
    },
    {
      field: "designation",
      headerName: "Designation",
      disableSort: true,
      renderCell: (row) => (
        <Select
          value={row.designation || ""}
          disabled={row.isAssigned || mode === "view"}
          onChange={(e) =>
            handleChapterDesignationChange(row._id, e.target.value)
          }
          size="small"
          displayEmpty
          sx={{ minWidth: 180 }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#9e9e9e" }}>Select designation</span>
              );
            }
            return CHAPTER_ROLE_OPTIONS.find((o) => o.value === selected)
              ?.label;
          }}
        >
          <MenuItem value="">
            <em>Select designation</em>
          </MenuItem>

          {CHAPTER_ROLE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: "assign",
      headerName: "Assign",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <button
          type="button"
          disabled={row.isAssigned || mode === "view"}
          onClick={() => handleAssignChapter(row._id)}
          style={{
            padding: "6px 12px",
            backgroundColor: row.isAssigned ? "#9e9e9e" : "#2e7d32",
            cursor: row.isAssigned ? "not-allowed" : "pointer",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
          }}
        >
          {row.isAssigned ? "Assigned" : "Assign"}
        </button>
      ),
    },
  ];

  useEffect(() => {
    committeeRows.forEach((row, index) => {
      const needsCommitteeOptions =
        !!row.council &&
        !row.hasLoadedCommitteeOptions &&
        !row.isLoadingCommitteeOptions;

      if (needsCommitteeOptions) {
        handleCouncilChange(index, row.council, {
          preserveSelection: true,
          suppressUserEdit: true,
        });
      }
    });
  }, [committeeRows]);

  useEffect(() => {
    committeeRows.forEach((row, index) => {
      const needsDesignationOptions =
        !!row.committee &&
        Array.isArray(row.committees) &&
        row.committees.length > 0 &&
        !row.hasLoadedDesignationOptions;

      if (needsDesignationOptions) {
        handleCommitteeChange(index, row.committee, {
          suppressUserEdit: true,
        });
      }
    });
  }, [committeeRows]);

  const deriveAuthorityTypes = (assignments = []) => {
    return [...new Set(assignments.map((a) => a.sourceType))];
  };

  const hydrateFromAssignments = (assignments) => {
    console.log("Hydrating from assignments:", assignments);
    let hydratedScopeCount = 0;

    setCommitteeRows((prevRows) => {
      const updatedRows = [...prevRows];

      assignments.forEach((a) => {
        if (a.sourceType !== "COMMITTEE") return;

        const sourceRef = getSourceRefId(a.sourceRef);

        const designationObject =
          a.designation && typeof a.designation === "object" ? a.designation : null;
        const assignmentDesignationId = String(
          designationObject?._id ||
            designationObject?.id ||
            designationObject?.designationId ||
            "",
        )
          .trim()
          .toLowerCase();

        const assignmentDesignation = getDesignationLabel(a.designation);
        const normalizedAssignmentDesignation = String(
          assignmentDesignation || "",
        )
          .trim()
          .toLowerCase();

        const rowMatchesDesignation = (r) => {
          const optionMatch = (r.designationOptions || []).some((d) => {
            const optionId = String(d?._id || "").trim().toLowerCase();
            const optionTitle = String(d?.title || "").trim().toLowerCase();
            if (assignmentDesignationId && assignmentDesignationId === optionId) {
              return true;
            }
            return (
              normalizedAssignmentDesignation &&
              (normalizedAssignmentDesignation === optionId ||
                normalizedAssignmentDesignation === optionTitle)
            );
          });

          if (optionMatch) return true;

          return (r.designations || []).some((id) =>
            String(id || "").trim().toLowerCase() === assignmentDesignationId,
          );
        };

        let row = updatedRows.find(
          (r) => String(r.committee || "") === String(sourceRef || ""),
        );

        if (!row) {
          row = updatedRows.find((r) => rowMatchesDesignation(r));
        }

        if (!row) return;

        const designation = (row.designationOptions || []).find((d) => {
          const optionId = String(d?._id || "").trim().toLowerCase();
          const optionTitle = String(d?.title || "").trim().toLowerCase();

          if (assignmentDesignationId && assignmentDesignationId === optionId) {
            return true;
          }

          return (
            normalizedAssignmentDesignation === optionTitle ||
            normalizedAssignmentDesignation === optionId
          );
        });

        const designationId =
          designation?._id ||
          (assignmentDesignationId
            ? (row.designations || []).find(
                (id) =>
                  String(id || "").trim().toLowerCase() ===
                  assignmentDesignationId,
              )
            : "") ||
          ((row.designations || []).length === 1 ? row.designations[0] : "");

        if (!designationId) return;

        const hasDesignation = (row.designations || []).some(
          (existingId) =>
            normalizeIdToken(existingId) === normalizeIdToken(designationId),
        );

        if (!hasDesignation) {
          row.designations.push(designationId);
        }

        if (
          assignmentDesignationId &&
          !(row.designationOptions || []).some(
            (d) =>
              String(d?._id || "").trim().toLowerCase() ===
              assignmentDesignationId,
          )
        ) {
          row.designationOptions = [
            ...(row.designationOptions || []),
            {
              _id: designationId,
              title:
                designationObject?.title ||
                designationObject?.name ||
                designationObject?.label ||
                designationObject?.policyRole ||
                assignmentDesignation ||
                designationId,
              label:
                designationObject?.label ||
                designationObject?.title ||
                designationObject?.name ||
                assignmentDesignation ||
                designationId,
              policyRole:
                designationObject?.policyRole || designationObject?.role || "",
              defaultPrivileges: normalizePrivileges(
                designationObject?.defaultPrivileges,
              ),
              privileges: normalizePrivileges(
                designationObject?.privileges || a?.privileges,
              ),
            },
          ];
        }

        if (!row.designationScopes) row.designationScopes = {};

        row.designationScopes[designationId] = {
          level: String(a.scope?.level || "NATIONAL").toUpperCase(),
          state: getScopeEntityId(a.scope?.state, ["_id", "stateId", "id"]),
          region: getScopeEntityId(a.scope?.region, ["_id", "regionId", "id"]),
          district: getScopeEntityId(a.scope?.district, [
            "_id",
            "districtId",
            "id",
          ]),
          chapter: a.scope?.chapter?._id || a.scope?.chapter || "",
          program: a.scope?.program?._id || a.scope?.program || "",
        };
        hydratedScopeCount += 1;

        if (!row.designationPrivileges) row.designationPrivileges = {};
        row.designationPrivileges[designationId] = {
          view: !!a.privileges?.view,
          create: !!a.privileges?.create,
          edit: !!a.privileges?.edit,
          delete: !!a.privileges?.delete,
          assign: !!a.privileges?.assign,
        };
      });

      // Defensive dedupe: avoid duplicate designation chips/scope blocks
      updatedRows.forEach((row) => {
        if (!Array.isArray(row.designations)) return;
        const seen = new Set();
        row.designations = row.designations.filter((designationId) => {
          const token = normalizeIdToken(designationId);
          if (!token || seen.has(token)) return false;
          seen.add(token);
          return true;
        });
      });

      return updatedRows;
    });

    return hydratedScopeCount > 0;
  };

  const hydrateProgramRows = async (assignments) => {
    const programAssignments = assignments.filter(
      (a) => a.sourceType === "PROGRAM",
    );
    if (!programAssignments.length) {
      setProgramRows([]);
      return;
    }

    const rows = await Promise.all(
      programAssignments.map(async (a) => {
        const rawBasketId = a.sourceRef || a.scope?.basket || "";
        const matchedBasket = programBaskets.find(
          (b) =>
            String(b._id || "") === String(rawBasketId || "") ||
            String(b.basketId || "") === String(rawBasketId || ""),
        );
        const basketMongoId = matchedBasket?._id || rawBasketId;
        const basketId = matchedBasket?.basketId || rawBasketId;
        const selectedProgramId = getSourceRefId(a.scope?.program) || "";
        let availablePrograms = [];

        if (basketId) {
          try {

            const res = await ApiCallService.get(
              `${process.env.REACT_APP_BASE_URL}/programs/baskets/${basketId}`,
            );
            availablePrograms = extractProgramsFromBasket(res);
          } catch (err) {
            console.error("Error hydrating basket programs:", err);
          }
        }

        const hasSelectedProgramInOptions =
          selectedProgramId &&
          availablePrograms.some((p) => String(p?._id || "") === String(selectedProgramId));

        if (selectedProgramId && !hasSelectedProgramInOptions) {
          const programFromScope = normalizeProgramOption(a.scope?.program);
          if (programFromScope) {
            availablePrograms = [...availablePrograms, programFromScope];
          } else {
            try {
              const programRes = await ApiCallService.get(
                `${process.env.REACT_APP_BASE_URL}/programs/${selectedProgramId}`,
              );
              const singleProgram = normalizeProgramOption(
                programRes?.data?.data ?? programRes?.data ?? programRes,
              );
              if (singleProgram) {
                availablePrograms = [...availablePrograms, singleProgram];
              }
            } catch (err) {
              console.error("Error fetching fallback program option:", err);
            }
          }
        }

        return {
          basketId,
          basketMongoId,
          programId: selectedProgramId,
          designation: "HEAD",
          availablePrograms,
          scope: {
            level: String(a.scope?.level || "").toUpperCase(),
            state: getSourceRefId(a.scope?.state) || "",
            region: getSourceRefId(a.scope?.region) || "",
            district: getSourceRefId(a.scope?.district) || "",
            chapter: getSourceRefId(a.scope?.chapter) || "",
          },
        };
      }),
    );

    setProgramRows(rows);
  };

  useEffect(() => {
    if (!userAssignments.length || !committeeRows.length) return;
    if (hasUserEditedCommitteeAssignmentsRef.current) return;
    if (hasHydratedAssignmentsRef.current) return;

    const committeeRowsWithSelections = committeeRows.filter(
      (row) => !!row.committee,
    );
    if (!committeeRowsWithSelections.length) return;

    const allCommitteeRowsReady = committeeRowsWithSelections.every((row) => {
      const hasCommitteeOptions =
        (Array.isArray(row.committees) && row.committees.length > 0) ||
        (Array.isArray(row.committeeStates) && row.committeeStates.length > 0);
      const hasDesignationOptions =
        Array.isArray(row.designationOptions) && row.designationOptions.length > 0;

      return hasCommitteeOptions && hasDesignationOptions;
    });
    if (!allCommitteeRowsReady) return;

    const didHydrate = hydrateFromAssignments(userAssignments);
    if (didHydrate) {
      hasHydratedAssignmentsRef.current = true;
    }
  }, [userAssignments, committeeRows]);

  useEffect(() => {
    if (!userAssignments.length) return;
    hydrateProgramRows(userAssignments);
  }, [userAssignments, programBaskets]);

  useEffect(() => {
    const chapterAssignments = (userAssignments || []).filter(
      (a) => a?.sourceType === "CHAPTER",
    );
    if (!chapterAssignments.length || !chapterRows.length) return;

    setChapterRows((prevRows) =>
      prevRows.map((row) => {
        const matched = chapterAssignments.find(
          (a) => String(getSourceRefId(a?.sourceRef)) === String(row._id),
        );
        if (!matched) return row;

        return {
          ...row,
          scope: {
            ...(row.scope || getDefaultChapterScope(row)),
            level: String(matched?.scope?.level || row.scope?.level || "NATIONAL")
              .trim()
              .toUpperCase(),
            state: getSourceRefId(matched?.scope?.state) || "",
            region: getSourceRefId(matched?.scope?.region) || "",
            district: getSourceRefId(matched?.scope?.district) || "",
            taluka: getSourceRefId(matched?.scope?.taluka) || "",
            chapter: row._id,
          },
        };
      }),
    );
  }, [userAssignments, chapterRows.length]);

  useEffect(() => {
    const stateIdsToFetch = new Set();

    committeeRows.forEach((row) => {
      const committeeStateId =
        typeof row?.committeeState === "object"
          ? row?.committeeState?._id || row?.committeeState?.id || ""
          : row?.committeeState;
      if (committeeStateId) {
        stateIdsToFetch.add(String(committeeStateId));
      }

      Object.values(row.designationScopes || {}).forEach((scope) => {
        const stateId =
          typeof scope?.state === "object"
            ? scope?.state?._id || scope?.state?.id || ""
            : scope?.state;
        if (stateId) {
          stateIdsToFetch.add(String(stateId));
        }
      });
    });

    programRows.forEach((row) => {
      const stateId =
        typeof row?.scope?.state === "object"
          ? row?.scope?.state?._id || row?.scope?.state?.id || ""
          : row?.scope?.state;
      if (stateId) {
        stateIdsToFetch.add(String(stateId));
      }
    });

    stateIdsToFetch.forEach((stateId) => {
      const hasRegions = Array.isArray(scopeRegionsByState[stateId]);
      const hasDistricts = Array.isArray(scopeDistrictsByState[stateId]);

      if (hasRegions && hasDistricts) return;
      if (scopeFetchInFlightRef.current.has(stateId)) return;

      scopeFetchInFlightRef.current.add(stateId);
      Promise.resolve(fetchScopeRegionsAndDistricts(stateId)).finally(() => {
        scopeFetchInFlightRef.current.delete(stateId);
      });
    });
  }, [
    committeeRows,
    programRows,
    scopeRegionsByState,
    scopeDistrictsByState,
  ]);

  useEffect(() => {
    if (!committeeRows.length) return;
    let shouldUpdate = false;
    const nextRows = committeeRows.map((row) => {
      if (!["REC", "DEC"].includes(row?.councilType) || !row?.committeeState) {
        return row;
      }
      const nextRegions = Array.isArray(scopeRegionsByState?.[row.committeeState])
        ? scopeRegionsByState[row.committeeState]
        : [];
      const nextDistricts =
        row.councilType === "DEC" &&
        Array.isArray(scopeDistrictsByState?.[row.committeeState])
          ? scopeDistrictsByState[row.committeeState]
          : row.committeeDistricts;
      const sameRegionsLength =
        Array.isArray(row.committeeRegions) &&
        row.committeeRegions.length === nextRegions.length;
      const sameDistrictsLength =
        row.councilType !== "DEC" ||
        (Array.isArray(row.committeeDistricts) &&
          row.committeeDistricts.length === nextDistricts.length);
      if (sameRegionsLength && sameDistrictsLength) return row;
      shouldUpdate = true;
      return {
        ...row,
        committeeRegions: nextRegions,
        committeeDistricts: nextDistricts,
      };
    });
    if (shouldUpdate) {
      setCommitteeRows(nextRows);
    }
  }, [scopeRegionsByState, scopeDistrictsByState, committeeRows, setCommitteeRows]);

  useEffect(() => {
    if (!userAssignments || userAssignments.length === 0) return;

    const derivedAuthorities = deriveAuthorityTypes(userAssignments);
    setAuthorityType(derivedAuthorities);
  }, [userAssignments]);

  useEffect(() => {
    if (!userExistingChapterAssignments.length) return;
    setAuthorityType((prev) =>
      prev.includes("CHAPTER") ? prev : [...prev, "CHAPTER"],
    );
  }, [userExistingChapterAssignments]);

  useEffect(() => {
    if (authorityType.length) return;
    const hasCommittee = committeeRows.some(
      (r) => r.committee || (r.designations && r.designations.length > 0),
    );
    if (hasCommittee) {
      setAuthorityType(["COMMITTEE"]);
    }
  }, [committeeRows, authorityType.length]);

  useEffect(() => {
    if (!id || mode === "create") return;

    const fetchAssignments = async () => {
      try {
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/userAssignments/user/${id}`,
        );

        console.log("Fetched user assignments:", res);
        const data = res?.data?.data ?? res?.data ?? res ?? [];
        hasHydratedAssignmentsRef.current = false;
        hasUserEditedCommitteeAssignmentsRef.current = false;
        setUserAssignments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load user assignments", err);
      }
    };

    fetchAssignments();
  }, [id, mode]);

  useEffect(() => {
    // Councils data is needed for display in all modes (view/edit/create)
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/committeeDetails/councils`)
      .then((res) => setCouncils(res?.data?.data ?? res?.data ?? []))
      .catch(console.error);
  }, []);

  const resetAssignmentsForCreate = () => {
    hasHydratedAssignmentsRef.current = false;
    hasUserEditedCommitteeAssignmentsRef.current = false;
    initialCommitteeMembershipsRef.current = [];
    setCommitteeRows([{ ...emptyCommitteeRow }]);
    setAuthorityType([]);
    setUserAssignments([]);
    setExistingUserChapterAssignments([]);
    setChapterRows([]);
    setProgramRows([]);
    setAddChapterAssignments(false);
    setAssignmentErrors([]);
  };

  const validateAssignments = () => {
    if (!authorityType.length) {
      return "";
    }

    if (authorityType.includes("COMMITTEE")) {
      const hasCommittee = committeeRows.some(
        (row) => row.committee && row.designations?.length > 0,
      );
      if (!hasCommittee) {
        return "Please select at least one committee and designation.";
      }
    }

    if (authorityType.includes("CHAPTER")) {
      const hasChapter = chapterRows.some((row) => row.isAssigned);
      if (!hasChapter) {
        return "Please assign at least one chapter role.";
      }
      const hasChapterScope = chapterRows.some(
        (row) => row.isAssigned && row.scope?.level,
      );
      if (!hasChapterScope) {
        return "Please select scope level for selected chapter assignment.";
      }
    }

    if (authorityType.includes("PROGRAM")) {
      const hasProgramBasket = programRows.some((row) => !!row.basketId);
      if (!hasProgramBasket) {
        return "Please select at least one program basket.";
      }

      const hasProgramSelected = programRows.some(
        (row) => row.basketId && row.programId,
      );
      if (!hasProgramSelected) {
        return "Please select at least one program under selected basket.";
      }

      const hasProgramScope = programRows.some(
        (row) => row.basketId && row.programId && row.scope?.level,
      );
      if (!hasProgramScope) {
        return "Please select scope level for selected program assignment.";
      }
    }

    return "";
  };

  const normalizeAssignment = (a) => ({
    sourceType: String(a?.sourceType || ""),
    sourceRef: String(getSourceRefId(a?.sourceRef) || ""),
    designation: String(getDesignationLabel(a?.designation) || ""),
    level: String(a?.scope?.level || ""),
    state: String(a?.scope?.state || ""),
    region: String(a?.scope?.region || ""),
    district: String(a?.scope?.district || ""),
    chapter: String(a?.scope?.chapter || ""),
    program: String(a?.scope?.program || ""),
    basket: String(a?.scope?.basket || ""),
  });

  const toComparable = (list = []) =>
    (Array.isArray(list) ? list : [])
      .map(normalizeAssignment)
      .sort((x, y) => {
        const ax = `${x.sourceType}|${x.sourceRef}|${x.designation}|${x.level}|${x.state}|${x.region}|${x.district}|${x.chapter}|${x.program}|${x.basket}`;
        const ay = `${y.sourceType}|${y.sourceRef}|${y.designation}|${y.level}|${y.state}|${y.region}|${y.district}|${y.chapter}|${y.program}|${y.basket}`;
        return ax.localeCompare(ay);
      });

  const hasAssignmentChanges = useMemo(() => {
    const hasAnySelectedAuthority = Array.isArray(authorityType) && authorityType.length > 0;
    const draftAssignments = hasAnySelectedAuthority
      ? buildUserAssignments(id || "draft-user")
      : [];
    const visibleCommitteeMembershipsChanged =
      mode !== "create" &&
      hasUserEditedCommitteeAssignmentsRef.current &&
      initialCommitteeMembershipsRef.current.length > 0 &&
      JSON.stringify(normalizeCommitteeMembershipRows(committeeRows)) !==
        JSON.stringify(initialCommitteeMembershipsRef.current);

    if (mode === "create") {
      return draftAssignments.length > 0;
    }

    const current = toComparable(draftAssignments);
    const existing = toComparable(userAssignments);
    return (
      visibleCommitteeMembershipsChanged ||
      JSON.stringify(current) !== JSON.stringify(existing)
    );
  }, [authorityType, committeeRows, chapterRows, programRows, userAssignments, mode, id]);

  return {
    councils,
    chapters,
    programs,
    chapterList,
    scopeRegionsByState,
    scopeDistrictsByState,
    committeeRows,
    setCommitteeRows,
    recordInitialCommitteeMemberships,
    authorityType,
    setAuthorityType,
    authorityOpen,
    setAuthorityOpen,
    assignmentErrors,
    setAssignmentErrors,
    runAssignments,
    syncUserAssignments,
    clearCommitteeAssignments,
    clearChapterAssignments,
    clearProgramAssignments,
    handleCouncilChange,
    handleCommitteeStateChange,
    handleCommitteeRegionChange,
    handleCommitteeDistrictChange,
    handleCommitteeSubtypeChange,
    handleCommitteeChange,
    handleDesignationChange,
    handleScopeChange,
    handleRemoveCommittee,
    handleAddCommitteeRow,
    chapterRows,
    programRows,
    programBaskets,
    chapterRoles,
    CHAPTER_ROLE_OPTIONS,
    handleAddProgramRow,
    handleRemoveProgramRow,
    handleProgramBasketChange,
    handleProgramSelectionChange,
    handleProgramScopeChange,
    handleChapterScopeChange,
    userExistingChapterAssignments,
    userAssignments,
    openAssignmentModal,
    setOpenAssignmentModal,
    selectedChapter,
    setSelectedChapter,
    addChapterAssignments,
    handleAddChapterAssignments,
    EXISTING_ASSIGNMENT_COLUMNS,
    EXISTING_ASSIGNMENT_ROWS,
    TESTcolumns,
    resetAssignmentsForCreate,
    validateAssignments,
    hasAssignmentChanges,
  };
};

export default useAssignments;
